import { Article } from "@/interfaces/Product";
import { Button } from "../ui/button";
import { Plus, Minus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";

interface ProductProps {
  product: Article;
  setValidProducts: React.Dispatch<React.SetStateAction<Article[]>>;
  disabled?: boolean;
  forLitige?: boolean;
}
const AdjustableProduct: React.FC<ProductProps> = ({
  product,
  setValidProducts,
  disabled,
  forLitige,
}) => {
  const [quantity, setQuantity] = useState<number>(1);

  const handleIncrease = () => {
    // First check if forLitige and if the new quantity would exceed product quantity
    if (forLitige && product.quantity && quantity + 1 > product.quantity) {
      toast({
        title: "Quantité maximale atteinte",
        variant: "destructive",
      });
      return;
    }

    // If we pass the check or forLitige is false, proceed with the increase
    setQuantity((prev) => prev + 1);
    setValidProducts((prev) => {
      if (prev.some((p) => p.genCode === product.genCode)) {
        return prev.map((p) =>
          p.genCode === product.genCode
            ? { ...p, quantity: (p.quantity ?? 0) + 1 }
            : p
        );
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
      setValidProducts((prev) =>
        prev.map((p) =>
          p.genCode === product.genCode
            ? { ...p, quantity: (p.quantity ?? 0) - 1 }
            : p
        )
      );
    }
    if (quantity === 1) {
      setValidProducts((prev) =>
        prev.filter((p) => p.genCode !== product.genCode)
      );
    }
  };

  useEffect(() => {
    if (forLitige) {
      setQuantity(product.quantity ?? 1);
    }
  }, []);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm mt-3">
      <div className="flex items-center gap-4">
        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
          <img
            src={product.image || "/placeholder.svg"}
            alt={product.articleName}
            className="h-full w-full object-cover "
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <h3 className="font-semibold text-gray-900">{product.genCode}</h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {product.articleName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleIncrease}
            disabled={disabled || quantity <= 0}
          >
            <Minus className="h-4 w-4" />
            <span className="sr-only">Decrease quantity</span>
          </Button>
          <span className="w-8 text-center font-medium">{quantity}</span>
          <Button
            variant="outline"
            size="icon"
            onClick={handleDecrease}
            disabled={disabled}
          >
            <Plus className="h-4 w-4" />
            <span className="sr-only">Increase quantity</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdjustableProduct;
